import React from 'react';
import { useLocation } from '@reach/router';

import Icon from '../../molecules/Icon';

import { color } from '../../atoms/colors';
import {
  InternalLink,
  ExternalLink,
  ScrollLink,
  LightboxButton,
  IconContainer,
} from './styles/CallToAction.styled';

import { useLightbox } from '../../context/lightboxContext';
import useCallToAction from './utils/useCallToAction';
import localizeUrl from './utils/localizeUrl';

const CallToAction = ({
  url,
  label,
  ariaLabel,
  styles,
  classes,
  children,
  variant,
  size,
  icon, // depracte this prop (use nextIcon instead)
  prevIcon,
  nextIcon,
  target,
  rel,
  disabled,
  scrollId,
  scrollDuration,
  scrollOffset,
  renderLightbox,
  lightbox,
  isFreeTrialLink,
}) => {
  const { pathname } = useLocation();
  const { origin, type } = useCallToAction(url);
  const { openLightbox } = useLightbox();

  const replaceTextWithSpan = inputText =>
    inputText.replace(/%([^%]+)%/g, '<span>$1</span>');

  if (renderLightbox && lightbox) {
    return (
      <LightboxButton
        type="button"
        aria-haspopup="true"
        variant={isFreeTrialLink ? 'Link - Free Trial' : variant}
        size={size || ''}
        styles={styles}
        className={`${classes || ''} ${disabled ? ' disabled' : ''}`}
        onClick={() => openLightbox(lightbox)}
      >
        {isFreeTrialLink ? (
          <>
            <div
              dangerouslySetInnerHTML={{ __html: replaceTextWithSpan(label) }}
            />
            <IconContainer>
              <Icon
                id="chevron-right"
                width="16px"
                height="16px"
                fillColor={color.blue[800]}
              />
            </IconContainer>
          </>
        ) : (
          <>
            {prevIcon && (
              <IconContainer>
                <Icon id={prevIcon} width="16px" height="16px" />
              </IconContainer>
            )}
            {children || label}
            {nextIcon || icon ? (
              <IconContainer>
                <Icon id={nextIcon || icon} width="16px" height="16px" />
              </IconContainer>
            ) : null}
          </>
        )}
      </LightboxButton>
    );
  }

  if (scrollId) {
    return (
      <ScrollLink
        to={scrollId}
        variant={variant}
        size={size || ''}
        styles={styles}
        data-link-type="scroll-link"
        className={`${classes || ''} ${disabled ? ' disabled' : ''}`}
        spy
        smooth
        duration={scrollDuration || 300}
        offset={scrollOffset || 0}
      >
        {prevIcon && (
          <IconContainer>
            <Icon id={prevIcon} width="16px" height="16px" />
          </IconContainer>
        )}
        {children || label}
        {nextIcon || icon ? (
          <IconContainer>
            <Icon id={nextIcon || icon} width="16px" height="16px" />
          </IconContainer>
        ) : null}
      </ScrollLink>
    );
  }

  if (!url || url === '') return null;

  if (type === 'internal') {
    const localizedUrl = localizeUrl(pathname, url);
    return (
      <InternalLink
        variant={isFreeTrialLink ? 'Link - Free Trial' : variant}
        size={size || ''}
        aria-label={ariaLabel || label}
        data-link-type="internal-link"
        className={`${classes || ''} ${disabled ? ' disabled' : ''}`}
        styles={styles}
        to={localizedUrl?.replace(origin, '')}
        onClick={() => localStorage.setItem('prevPage', pathname)}
      >
        {isFreeTrialLink ? (
          <>
            <div
              dangerouslySetInnerHTML={{ __html: replaceTextWithSpan(label) }}
            />
            <IconContainer>
              <Icon
                id="chevron-right"
                width="16px"
                height="16px"
                fillColor={color.blue[800]}
              />
            </IconContainer>
          </>
        ) : (
          <>
            {prevIcon && (
              <IconContainer>
                <Icon id={prevIcon} width="16px" height="16px" />
              </IconContainer>
            )}
            {children || label}
            {nextIcon || icon ? (
              <IconContainer>
                <Icon id={nextIcon || icon} width="16px" height="16px" />
              </IconContainer>
            ) : null}
          </>
        )}
      </InternalLink>
    );
  }

  return (
    <ExternalLink
      variant={isFreeTrialLink ? 'Link - Free Trial' : variant}
      size={size || ''}
      aria-label={ariaLabel || label}
      data-link-type="external-link"
      className={`${classes || ''} ${disabled ? ' disabled' : ''}`}
      styles={styles}
      href={url}
      target={target || '_self'}
      rel={rel || ''}
      onClick={() => localStorage.setItem('prevPage', pathname)}
    >
      {isFreeTrialLink ? (
        <>
          <div
            dangerouslySetInnerHTML={{ __html: replaceTextWithSpan(label) }}
          />
          <IconContainer>
            <Icon
              id="chevron-right"
              width="16px"
              height="16px"
              fillColor={color.blue[800]}
            />
          </IconContainer>
        </>
      ) : (
        <>
          {prevIcon && (
            <IconContainer>
              <Icon id={prevIcon} width="16px" height="16px" />
            </IconContainer>
          )}
          {children || label}
          {nextIcon || icon ? (
            <IconContainer>
              <Icon id={nextIcon || icon} width="16px" height="16px" />
            </IconContainer>
          ) : null}
        </>
      )}
    </ExternalLink>
  );
};

export default CallToAction;
